import { HeartHandshake, Scale, ShieldCheck, Sparkles, Users, BookOpen } from "lucide-react";
import { PageHero, SectionHeading } from "../components/shared/Primitives";

const VALUES = [
  { icon: Scale, title: "Fairness", body: "Carers should earn properly. Families shouldn't be overcharged. Both are possible." },
  { icon: Sparkles, title: "Clarity", body: "Plain English. Written expectations. No surprises." },
  { icon: ShieldCheck, title: "Protection", body: "For carers, for families, for the people being cared for. Structure prevents harm." },
  { icon: BookOpen, title: "Professionalism", body: "Independent does not mean amateur. Standards matter." },
  { icon: HeartHandshake, title: "Independence", body: "More carers working for themselves. More families in control of their care." },
  { icon: Users, title: "Human dignity", body: "The person receiving care is not the subject of a transaction. They're the point of all of it." },
];

export default function About() {
  return (
    <div data-testid="page-about">
      <PageHero
        eyebrow="About JCC"
        title="Built from real care, not a boardroom."
        description="Just Care Community was created from real-life care experience — and real-life frustration with a system where carers are underpaid, families are overcharged, and both sides often lack clear guidance."
      />

      <section className="section">
        <div className="container-jcc grid lg:grid-cols-2 gap-12">
          <div>
            <SectionHeading eyebrow="Our mission" title="Make independent care safer, fairer and clearer — for everyone."
              description="We're not trying to replace regulated care, displace agencies, or pretend private care is always the answer. We're trying to make the existing independent arrangements — which millions of families already use — actually work properly." />
            <p className="mt-6 text-ink-muted leading-relaxed">
              Independent care isn't new. Families have hired carers directly for decades. What's new is the tooling, the templates, the education and the community to do it well — without needing to qualify as a lawyer, accountant or HR specialist along the way.
            </p>
          </div>
          <div className="rounded-3xl bg-surface p-10">
            <p className="font-display text-2xl leading-snug">
              &ldquo;JCC isn't an agency. It's a resource hub that treats carers as professionals, and families as partners — not customers.&rdquo;
            </p>
            <p className="mt-4 text-sm text-ink-muted">— JCC Founding Team</p>
          </div>
        </div>
      </section>

      <section className="section bg-surface">
        <div className="container-jcc max-w-3xl" data-testid="why-jcc-exists">
          <SectionHeading
            eyebrow="Why JCC exists"
            title="Why JCC Exists"
          />
          <div className="mt-8 space-y-6 text-ink-muted leading-relaxed text-base md:text-lg">
            <p>
              For years, independent carers and families have been trying to
              navigate private care with very little guidance.
            </p>
            <p>
              Families are often overwhelmed.<br />
              Carers are often underpaid, unsupported, or blamed when
              arrangements break down.
            </p>
            <p>
              And important topics — contracts, expectations, working hours,
              boundaries, safety, rates, and responsibilities — are often
              treated as awkward conversations instead of essential ones.
            </p>
            <p>
              JCC was created to make independent care safer, clearer, and more
              sustainable for everyone involved.
            </p>
            <p>
              Not by replacing regulated care.<br />
              Not by pretending agencies are unnecessary.
            </p>
            <p>
              But by recognising that direct care arrangements already happen
              every day — and helping people do them properly.
            </p>
          </div>

          <div
            className="mt-12 border-l-2 border-teal/60 pl-6 py-1"
            data-testid="why-jcc-trust-line"
          >
            <p className="font-display text-xl md:text-2xl text-ink leading-snug">
              Built by people with real care experience.
            </p>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-jcc">
          <SectionHeading eyebrow="Our values" title="Six things we won't compromise on." />
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {VALUES.map((v) => (
              <div key={v.title} className="card-base p-6" data-testid={`value-${v.title.toLowerCase()}`}>
                <div className="w-11 h-11 rounded-xl bg-teal-50 text-teal flex items-center justify-center mb-4">
                  <v.icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <h3 className="font-display text-xl font-semibold">{v.title}</h3>
                <p className="mt-2 text-ink-muted text-sm leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
